import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Check, ConciergeBell, Loader2, RotateCcw, UserRound, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { locale, tr } from "@/i18n";
import { pushDataLayer } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHAT_KEY = "dc_live_chat_id";

const readEvents = async (response, onToken, onState) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let assistantText = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";
    for (const block of events) {
      const event = block.match(/^event: (.+)$/m)?.[1];
      const dataLine = block.match(/^data: ([\s\S]+)$/m)?.[1];
      if (!event || !dataLine) continue;
      const payload = JSON.parse(dataLine);
      if (event === "token") {
        assistantText += payload.content;
        onToken(assistantText);
      } else if (event === "state") {
        onState(payload);
      } else if (event === "error") {
        throw new Error(payload.detail || "Live chat is temporarily unavailable");
      }
    }
  }
};

export function PublicLiveChat() {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  const loadSession = async () => {
    try {
      const existing = localStorage.getItem(CHAT_KEY);
      const { data } = await axios.post(`${API}/live-chat/session`, { conversation_id: existing || undefined, language: locale() });
      localStorage.setItem(CHAT_KEY, data.conversation_id);
      setSession(data);
      setMessages(data.messages || []);
    } catch {
      localStorage.removeItem(CHAT_KEY);
      const { data } = await axios.post(`${API}/live-chat/session`, { language: locale() });
      localStorage.setItem(CHAT_KEY, data.conversation_id);
      setSession(data);
      setMessages(data.messages || []);
    }
  };

  useEffect(() => {
    if (open && !session) loadSession().catch(() => setError(tr("Live chat is temporarily unavailable")));
  }, [open, session]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, session?.ready_for_review, session?.status]);

  const reviewRows = useMemo(() => {
    const lead = session?.lead || {};
    return [
      [tr("First Name"), lead.first_name],
      [tr("Last Name"), lead.last_name],
      [tr("Phone"), lead.phone],
      [tr("Email"), lead.email],
      [tr("Requested Services"), Array.isArray(lead.requested_services) ? lead.requested_services.map((s) => tr(s)).join(", ") : ""],
      [tr("How can we help?"), lead.help_needed],
    ].filter(([, value]) => value);
  }, [session]);

  const send = async (text = input) => {
    const message = text.trim();
    if (!message || streaming || !session) return;
    setInput("");
    setError("");
    setStreaming(true);
    setMessages((current) => [...current, { role: "user", content: message }, { role: "assistant", content: "", pending: true }]);
    try {
      const response = await fetch(`${API}/live-chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: session.conversation_id, message, language: locale() }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail || tr("The assistant could not respond right now"));
      }
      await readEvents(
        response,
        (content) => setMessages((current) => current.map((m, index) => index === current.length - 1 ? { ...m, content, pending: true } : m)),
        (nextSession) => {
          setSession(nextSession);
          setMessages((current) => current.map((m, index) => index === current.length - 1 ? { ...m, pending: false } : m));
        },
      );
    } catch (err) {
      setMessages((current) => current.filter((m) => !m.pending));
      setError(err.message);
    } finally {
      setStreaming(false);
    }
  };

  const startSalesIQ = async () => {
    const lead = session?.lead || {};
    const salesiq = window.$zoho?.salesiq;
    salesiq?.visitor?.name?.({ firstname: lead.first_name || "", lastname: lead.last_name || "" });
    salesiq?.visitor?.email?.(lead.email || "");
    salesiq?.visitor?.contactnumber?.(lead.phone || "");
    document.body.classList.add("salesiq-live");
    await axios.post(`${API}/live-chat/escalate`, { conversation_id: session.conversation_id, language: locale() }).catch(() => {});
    salesiq?.floatbutton?.visible?.("show");
    salesiq?.floatwindow?.visible?.("show");
    salesiq?.chat?.start?.();
  };

  const submit = async () => {
    if (!session?.ready_for_review || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/live-chat/finalize`, { conversation_id: session.conversation_id, language: locale() });
      setSession(data);
      toast.success(tr("Your enquiry has been received"), { description: tr("Our team will review your request and respond shortly.") });
      pushDataLayer({
        event: "enquiry_submitted",
        enquiry_type: "live_chat",
        requested_service: Array.isArray(data.lead?.requested_services) ? data.lead.requested_services[0] : undefined,
        form_location: window.location.pathname,
        lead_source: "LIVE CHAT",
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : tr("We could not submit your enquiry yet"));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = async () => {
    if (streaming || submitting) return;
    localStorage.removeItem(CHAT_KEY);
    const { data } = await axios.post(`${API}/live-chat/session`, { language: locale() });
    localStorage.setItem(CHAT_KEY, data.conversation_id);
    setSession(data);
    setMessages([]);
    setError("");
  };

  return (
    <>
      {!open && (
        <button type="button" data-testid="live-chat-open-button" onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-[76] flex items-center gap-3 bg-gold text-black px-5 py-4 text-[0.62rem] uppercase tracking-[0.25em] shadow-[0_18px_50px_rgba(198,169,98,0.28)] hover:bg-gold-bright transition-colors duration-300">
          <ConciergeBell strokeWidth={1.5} size={18} />
          {tr("Live Chat")}
        </button>
      )}
      <AnimatePresence>
        {open && (
          <motion.aside data-testid="live-chat-panel" className="fixed bottom-24 right-6 z-[78] w-[calc(100vw-3rem)] max-w-[430px] h-[min(680px,calc(100vh-8rem))] bg-[#09090A]/96 backdrop-blur-xl border border-gold/25 shadow-[0_30px_100px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden" initial={{ opacity: 0, y: 28, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
            <div className="p-5 border-b border-white/10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full border border-gold/45 bg-gold/10 flex items-center justify-center text-gold"><UserRound strokeWidth={1.5} size={22} /></div>
              <div className="flex-1">
                <p className="font-serif text-xl font-light" data-testid="live-chat-title">{tr("Delux Concierge")}</p>
                <p className="text-[0.58rem] uppercase tracking-[0.25em] text-gold/80 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {tr("Online Assistant")}</p>
              </div>
              <button type="button" data-testid="live-chat-reset-button" onClick={reset} className="text-white/35 hover:text-gold transition-colors" aria-label={tr("New chat")}><RotateCcw strokeWidth={1.5} size={17} /></button>
              <button type="button" data-testid="live-chat-close-button" onClick={() => setOpen(false)} className="text-white/45 hover:text-gold transition-colors" aria-label={tr("Close live chat")}><X strokeWidth={1.5} size={20} /></button>
            </div>

            <div ref={scrollRef} className="modal-scroll flex-1 overflow-y-auto p-5 space-y-4" data-testid="live-chat-messages">
              {messages.length === 0 && (
                <div className="border border-white/10 bg-white/[0.03] p-5">
                  <p className="text-sm font-light leading-relaxed text-white/78">{tr("Good day. How may Delux Concierge assist you today?")}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {[tr("Ask about membership"), tr("Arrange a service"), tr("Speak to the team")].map((prompt) => (
                      <button key={prompt} type="button" data-testid={`live-chat-prompt-${prompt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => send(prompt)} className="border border-gold/25 text-gold/85 text-[0.58rem] uppercase tracking-[0.18em] px-3 py-2 hover:border-gold/60 transition-colors">{prompt}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((message, index) => (
                <div key={`${message.created_at || index}-${index}`} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[86%] p-4 text-sm font-light leading-relaxed ${message.role === "user" ? "bg-gold text-black" : "border border-white/10 bg-white/[0.04] text-white/82"}`} data-testid={`live-chat-message-${message.role}`}>{message.content}{message.pending && <Loader2 className="inline-block ml-2 animate-spin" size={13} />}</div>
                </div>
              ))}
              {session?.ready_for_review && (
                <div className="border border-gold/35 bg-gold/[0.06] p-5" data-testid="live-chat-review-card">
                  <p className="text-[0.62rem] uppercase tracking-[0.28em] text-gold mb-3">{tr("Review & Confirm")}</p>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {reviewRows.map(([label, value]) => <p key={label} className="text-xs font-light"><span className="block uppercase tracking-[0.2em] text-white/38 mb-1">{label}</span><span className="text-white/82">{value}</span></p>)}
                  </div>
                  {session.escalation === "YES" && <p className="text-gold/80 text-xs font-light mt-4">{tr("A member of our team will be brought in after submission.")}</p>}
                  <button type="button" data-testid="live-chat-submit-button" onClick={submit} disabled={submitting} className="mt-5 w-full bg-gold text-black py-3.5 text-[0.62rem] uppercase tracking-[0.28em] hover:bg-gold-bright transition-colors disabled:opacity-60">{submitting ? tr("Submitting…") : tr("Confirm & Submit Enquiry")}</button>
                </div>
              )}
              {session?.status === "submitted" && (
                <div className="border border-gold/30 bg-gold/[0.05] p-5" data-testid="live-chat-submitted-card">
                  <p className="text-sm text-white/80 font-light flex items-center gap-2"><Check size={16} className="text-gold" /> {tr("Your enquiry has been submitted.")}</p>
                  {session.escalation === "YES" && <button type="button" data-testid="live-chat-salesiq-button" onClick={startSalesIQ} className="mt-4 w-full border border-gold/40 text-gold py-3 text-[0.62rem] uppercase tracking-[0.25em] hover:bg-gold/10 transition-colors">{tr("Continue to Live Team")}</button>}
                </div>
              )}
              {error && <p data-testid="live-chat-error" className="text-red-400/90 text-xs text-center">{error}</p>}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-white/10 flex items-center gap-3">
              <input data-testid="live-chat-message-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tr("How can we assist you?")} className="flex-1 bg-white/[0.04] border border-white/10 px-4 py-3 text-sm font-light outline-none focus:border-gold/50" disabled={streaming || session?.status === "submitted"} />
              <button type="submit" data-testid="live-chat-send-button" disabled={!input.trim() || streaming || session?.status === "submitted"} className="w-11 h-11 bg-gold text-black flex items-center justify-center hover:bg-gold-bright transition-colors disabled:opacity-50" aria-label={tr("Send message")}>{streaming ? <Loader2 className="animate-spin" size={17} /> : <ArrowUp strokeWidth={1.75} size={18} />}</button>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
